import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { FlowEventEmitter } from '../../editor-composition/useFlowEvents';
import type { NodeSpec } from './NodeSpec';

export const functionalInputsUiNodeSpec: NodeSpec<NodeCfg<'functionalInputs'>> = {
    canCopy: false,
    canDelete: false,
    suppressNode: true,
    inputs: () => ({}),
    nodeCategory: 'function',
    kindLabel: 'Functional Inputs',
    validator: node => {
        const names = node.controlValues.inputs.map(input => input.name);
        if (!node.controlValues.inputs.every(input => input.name)) {
            return { inputs: 'One or more input names are empty' };
        }
        if (new Set(names).size !== names.length) {
            return { inputs: 'One or more inputs share a name' };
        }
        return true;
    },
    requiredSockets: node => {
        const availableOptions = [ 'start', ...node.controlValues.inputs.map((_, index) => `output${index}`) ] as ('start' | `output${number}`)[];
        // this conditionallyRequired statement basically says at least one output socket must be wired up
        return { required: [], conditionallyRequired: [ { kind: 'or', availableOptions } ] };
    },
    cfg: () => ({
        controlValues: {
            inputs: [],
        },
        id: getUID(),
        disabled: false,
        kind: 'functionalInputs',
    }),
    controls: nodeCfg => ({
        inputs: defineControlOpts({
            cfg: {
                kind: 'button',
                useDialog: null,
                label: 'Add Input',
            },
            on: {
                'update:value': () => {}, // no-op, this control does not use the update:value event
                'click': (_, { flowEventEmitter }) => {
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                inputs: [
                                    ...nodeCfg.controlValues.inputs,
                                    { name: '', dataType: 'string' },
                                ],
                            },
                        },
                    });
                },
            },
            index: 1,
            value: nodeCfg.controlValues.inputs,
        }),
    }),
    outputs: nodeCfg => {
        const updateInputAt = (index: number, name: string, dataType: NodeCfg<'functionalInputs'>['controlValues']['inputs'][number]['dataType'], flowEventEmitter: FlowEventEmitter) => {
            flowEventEmitter.emit('updateNodeCfg', {
                newNodeCfg: {
                    ...nodeCfg,
                    controlValues: {
                        ...nodeCfg.controlValues,
                        inputs: nodeCfg.controlValues.inputs.map((item, itemIndex) => itemIndex === index ? { name, dataType } : item),
                    },
                },
            });
        };

        const outputs: ReturnType<NodeSpec<NodeCfg<'functionalInputs'>>['outputs']> = {
            start: { socket: 'event', label: 'Start' },
        };

        nodeCfg.controlValues.inputs.forEach((input, index) => {
            outputs[`output${index}`] = {
                socket: input.dataType,
                controlOpts: defineControlOpts({
                    cfg: {
                        valuePlaceholder: 'Input Name',
                        selectionPlaceholder: 'Input Type',
                        kind: 'deletableTextControlWithTypeSelector',
                        allowedDataTypes: [ 'string', 'number', 'boolean' ],
                    },
                    value: { newValue: input.name, dataType: input.dataType },
                    enableWithConnectedSocket: true,
                    on: {
                        'update:value': (change, { flowEventEmitter }) => updateInputAt(index, change.newValue, change.dataType, flowEventEmitter),
                        'delete': (_, { flowEventEmitter }) => flowEventEmitter.emit('removeNodeSocket', {
                            direction: 'output',
                            removalMethod: {
                                index,
                                prefix: 'output',
                                kind: 'indexedIdentifier',
                            },
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    inputs: nodeCfg.controlValues.inputs.filter((_, itemIndex) => itemIndex !== index),
                                },
                            },
                        }),
                    },
                }),
            };
        });

        return outputs;
    },
};
